/**
 * Dictation — the browser's speech recognition typing into a field
 * (`docs/activity.md` §11.1, F8).
 *
 * Moved here from `property-map/crew/add-mark.tsx` so the crew's mark, the
 * call log, New task, New note and every caption on a phone use one
 * implementation. Three looks, and the default is add-mark's exactly:
 *
 * - `'button'` — add-mark's large button, *Voice note* / *Listening… tap to stop*.
 * - `'chip'` — the 28 px *Dictate* chip with a mic (Boards 11, 14 C).
 * - `'icon'` — a mic glyph inside a field (Boards 15 B, 16 B, 16 E).
 *
 * While listening the chip and the icon become the red pill *Listening… tap
 * to stop* (Board 14 C). **Absent where the browser has no speech
 * recognition** rather than disabled: a control that asks for a microphone
 * and then does nothing is worse than one that was never offered. Vendor-
 * prefixed on Safari, which is most of the iPads and iPhones this runs on.
 * **Nothing is recorded** — Dictate is words, never audio.
 */
import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn.js'
import { Button } from './primitives.js'

export type DictationVariant = 'button' | 'chip' | 'icon'

export function Dictation({
  onText, label = 'Voice note', variant = 'button', className,
}: {
  /** Each finished phrase, trimmed. */
  onText: (said: string) => void
  /** The button's word (`'button'` only). */
  label?: string
  variant?: DictationVariant
  className?: string
}) {
  const [listening, setListening] = useState(false)
  const [offered, setOffered] = useState(false)
  const session = useRef<{ stop: () => void } | null>(null)

  useEffect(() => {
    const api = speechApi()
    setOffered(Boolean(api))
    return () => session.current?.stop()
  }, [])

  if (!offered) return null

  const listen = () => {
    const Api = speechApi()
    if (!Api) return
    const run = new Api() as SpeechLike
    run.lang = 'en-US'
    run.interimResults = false
    run.onresult = (event) => {
      const said = event.results[0]?.[0]?.transcript
      if (said) onText(said.trim())
    }
    run.onend = () => setListening(false)
    run.onerror = () => setListening(false)
    session.current = { stop: () => run.stop() }
    run.start()
    setListening(true)
  }
  const toggle = () => (listening ? session.current?.stop() : listen())

  if (variant === 'button') {
    return (
      <Button size="big" className={cn('min-h-12', className)} onClick={toggle}>
        {listening ? 'Listening… tap to stop' : label}
      </Button>
    )
  }

  if (listening) {
    return (
      <button
        type="button"
        onClick={toggle}
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 rounded-full bg-danger-bg font-bold text-danger',
          variant === 'chip' ? 'h-9 px-3 text-[13px]' : 'h-7 px-2.5 text-[12px]',
          className,
        )}
      >
        <i aria-hidden className="block h-2 w-2 rounded-full bg-danger" />
        Listening… tap to stop
      </button>
    )
  }

  if (variant === 'chip') {
    return (
      <button
        type="button"
        onClick={toggle}
        className={cn(
          'inline-flex h-7 shrink-0 items-center gap-[5px] rounded-[7px] border border-line bg-surface px-[9px]',
          'text-[12px] font-semibold text-text-body hover:bg-hover',
          className,
        )}
      >
        <Mic size={13} />
        Dictate
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Dictate"
      className={cn('inline-flex shrink-0 items-center justify-center text-accent', className)}
    >
      <Mic size={17} stroke={2.1} />
    </button>
  )
}

/** The boards' microphone. */
function Mic({ size, stroke = 2 }: { size: number; stroke?: number }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
      strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10v1a7 7 0 0 0 14 0v-1M12 18v4M8 22h8" />
    </svg>
  )
}

interface SpeechLike {
  lang: string
  interimResults: boolean
  onresult: (event: { results: ArrayLike<ArrayLike<{ transcript?: string }>> }) => void
  onend: () => void
  onerror: () => void
  start: () => void
  stop: () => void
}

/** The browser's speech recognition, prefixed or not — null where there is none. */
export function speechApi(): (new () => unknown) | null {
  if (typeof window === 'undefined') return null
  const host = window as unknown as {
    SpeechRecognition?: new () => unknown
    webkitSpeechRecognition?: new () => unknown
  }
  return host.SpeechRecognition ?? host.webkitSpeechRecognition ?? null
}
